import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Loader2, Lock, Unlock, RefreshCw, FileSpreadsheet } from 'lucide-react';
import { safeJson } from '../lib/safeJson';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => (v === 0 || v ? Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 }) : '—');

const CTC_COLS = [['ctc_pay', 'PAY'], ['ctc_agp', 'AGP'], ['ctc_gross_basic', 'GROSS BASIC'], ['ctc_da', 'D.A.'], ['ctc_hra', 'H.R.A.'], ['ctc_cla', 'C.L.A.'], ['ctc_hod', 'H.O.D.'], ['ctc_special', 'SPECIAL'], ['ctc_gross', 'GROSS']];
const EARNED_COLS = [['present_days', 'DAYS'], ['basic_e', 'BASIC'], ['da_e', 'D.A.'], ['hra_e', 'H.R.A.'], ['cla_e', 'C.L.A.'], ['hod_e', 'H.O.D.'], ['special_e', 'SPECIAL'], ['arrears', 'ARREARS'], ['gross_e', 'GROSS']];
const DED_COLS = [['pf', 'PF'], ['pt', 'PROF. TAX'], ['esic', 'ESIC'], ['bus', 'BUS'], ['tds', 'TDS'], ['advance', 'ADVANCE'], ['sq', 'QUARTERS'], ['elec', 'ELECTRICITY'], ['lm', 'LOCAL MART'], ['tot_ded', 'TOTAL DED.']];
const TOTAL_KEYS = { ctc_pay: 'pay', ctc_agp: 'agp', ctc_gross_basic: 'gross_basic', ctc_da: 'da', ctc_hra: 'hra', ctc_cla: 'cla', ctc_hod: 'hod', ctc_special: 'special', ctc_gross: 'gross_ctc' };

export const PayrollReviewSheet = ({ selectedEntity, month, canLock, onLockChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dept, setDept] = useState('all');
  const [desig, setDesig] = useState('all');
  const [locking, setLocking] = useState(false);

  const headers = () => {
    const token = localStorage.getItem('hrms-token');
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  };

  const fetchSheet = useCallback(async () => {
    if (!selectedEntity || !month) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ entity_id: selectedEntity.entity_id, month });
      if (dept !== 'all') params.append('department', dept);
      if (desig !== 'all') params.append('designation', desig);
      const res = await fetch(`${API_URL}/api/payroll/review-sheet?${params}`, { headers: headers(), credentials: 'include' });
      const d = await safeJson(res);
      if (res.ok) {
        setData(d);
        onLockChange?.(!!d.lock?.locked);
      } else toast.error(d.detail || 'Failed to load review sheet');
    } catch { toast.error('Failed to load review sheet'); }
    finally { setLoading(false); }
  }, [selectedEntity, month, dept, desig]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchSheet(); }, [fetchSheet]);

  const toggleLock = async (action) => {
    if (action === 'lock' && !window.confirm(`Lock the payroll sheet for ${month}? Downloads will become available and the sheet is marked reviewed.`)) return;
    setLocking(true);
    try {
      const res = await fetch(`${API_URL}/api/payroll/sheet-lock`, {
        method: 'POST', headers: headers(), credentials: 'include',
        body: JSON.stringify({ entity_id: selectedEntity.entity_id, month, action })
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
      fetchSheet();
    } catch (e) { toast.error(e.message); }
    finally { setLocking(false); }
  };

  const locked = !!data?.lock?.locked;
  const rows = data?.rows || [];
  const t = data?.totals || {};

  return (
    <Card data-testid="payroll-review-sheet">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
            Salary Sheet Review — {month} <Badge variant="outline">{data?.count ?? 0} employees</Badge>
            {locked && <Badge className="bg-emerald-100 text-emerald-800" data-testid="sheet-locked-badge"><Lock className="w-3 h-3 mr-1" />Locked by {data.lock.locked_by_name}</Badge>}
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={dept} onValueChange={setDept}>
              <SelectTrigger className="w-[170px] h-9" data-testid="sheet-dept-filter"><SelectValue placeholder="Department" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {(data?.departments || []).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={desig} onValueChange={setDesig}>
              <SelectTrigger className="w-[170px] h-9" data-testid="sheet-desig-filter"><SelectValue placeholder="Designation" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Designations</SelectItem>
                {(data?.designations || []).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={fetchSheet} data-testid="sheet-refresh"><RefreshCw className="w-4 h-4" /></Button>
            {canLock && !locked && rows.length > 0 && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={locking}
                onClick={() => toggleLock('lock')} data-testid="lock-sheet-btn">
                {locking ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Lock className="w-4 h-4 mr-1" />}Lock Sheet
              </Button>
            )}
            {canLock && locked && (
              <Button size="sm" variant="outline" className="text-amber-700 border-amber-400" disabled={locking}
                onClick={() => toggleLock('unlock')} data-testid="unlock-sheet-btn">
                <Unlock className="w-4 h-4 mr-1" />Unlock
              </Button>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Review every value against the master data. Once cross-checked, <strong>Lock Sheet</strong> — the Salary Statement &amp; Bank Export downloads appear after locking.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-10" data-testid="sheet-empty">No payroll records for this month/filter. Generate payroll first.</p>
        ) : (
          <div className="overflow-x-auto border rounded-md" data-testid="sheet-table-wrap">
            <table className="text-[11px] whitespace-nowrap w-full">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th colSpan={3} className="px-2 py-1.5 text-left border border-slate-600">EMPLOYEE</th>
                  <th colSpan={CTC_COLS.length} className="px-2 py-1.5 border border-slate-600">MONTHLY CTC (MASTER)</th>
                  <th colSpan={EARNED_COLS.length} className="px-2 py-1.5 border border-slate-600">EARNED THIS MONTH</th>
                  <th colSpan={DED_COLS.length} className="px-2 py-1.5 border border-slate-600">DEDUCTIONS</th>
                  <th className="px-2 py-1.5 border border-slate-600">NET</th>
                </tr>
                <tr className="bg-slate-100 text-slate-700">
                  <th className="px-2 py-1 border text-left">SR</th>
                  <th className="px-2 py-1 border text-left">NAME</th>
                  <th className="px-2 py-1 border text-left">DESIGNATION</th>
                  {CTC_COLS.map(([k, l]) => <th key={k} className="px-2 py-1 border text-right">{l}</th>)}
                  {EARNED_COLS.map(([k, l]) => <th key={k} className="px-2 py-1 border text-right bg-emerald-50">{l}</th>)}
                  {DED_COLS.map(([k, l]) => <th key={k} className="px-2 py-1 border text-right bg-red-50">{l}</th>)}
                  <th className="px-2 py-1 border text-right bg-indigo-50 font-bold">NET PAYABLE</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.payroll_id} className="hover:bg-amber-50 even:bg-slate-50/50" data-testid={`sheet-row-${r.employee_id}`}>
                    <td className="px-2 py-1 border">{i + 1}</td>
                    <td className="px-2 py-1 border font-medium">{r.name}<span className="block text-[9px] text-slate-400">{r.department || ''}</span></td>
                    <td className="px-2 py-1 border">{r.designation || '—'}</td>
                    {CTC_COLS.map(([k]) => <td key={k} className="px-2 py-1 border text-right font-mono">{fmt(r[k])}</td>)}
                    {EARNED_COLS.map(([k]) => <td key={k} className="px-2 py-1 border text-right font-mono bg-emerald-50/40">{k === 'present_days' ? r[k] : fmt(r[k])}</td>)}
                    {DED_COLS.map(([k]) => <td key={k} className="px-2 py-1 border text-right font-mono bg-red-50/40">{fmt(r[k])}</td>)}
                    <td className="px-2 py-1 border text-right font-mono font-bold text-indigo-700 bg-indigo-50/50">{fmt(r.net)}</td>
                  </tr>
                ))}
                <tr className="bg-slate-800 text-white font-bold" data-testid="sheet-totals-row">
                  <td className="px-2 py-1.5 border border-slate-600"></td>
                  <td className="px-2 py-1.5 border border-slate-600">TOTAL</td>
                  <td className="px-2 py-1.5 border border-slate-600"></td>
                  {CTC_COLS.map(([k]) => <td key={k} className="px-2 py-1.5 border border-slate-600 text-right font-mono">{fmt(t[TOTAL_KEYS[k]])}</td>)}
                  {EARNED_COLS.map(([k]) => <td key={k} className="px-2 py-1.5 border border-slate-600 text-right font-mono">{k === 'present_days' ? '' : fmt(t[k])}</td>)}
                  {DED_COLS.map(([k]) => <td key={k} className="px-2 py-1.5 border border-slate-600 text-right font-mono">{fmt(t[k])}</td>)}
                  <td className="px-2 py-1.5 border border-slate-600 text-right font-mono text-emerald-300">{fmt(t.net)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PayrollReviewSheet;
